import React, { useEffect, useRef } from 'react';

function BottomSheet({ isOpen, onClose, height = '50%', children, title }) {
  const sheetRef = useRef(null);
  const handleRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDraggingHandle = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleTouchStart = (e) => {
      const target = e.target;
      const isHandle = handleRef.current && handleRef.current.contains(target);
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      const isButton = target.tagName === 'BUTTON' || target.closest('button');
      const isClickable = target.closest('.ponto-item') || target.closest('.route-option-card');
      const isScrollable = target.closest('.bottom-sheet-content');
      
      if (isInput || isButton || isClickable) {
        isDraggingHandle.current = false;
        return;
      }
      
      if (isHandle) {
        isDraggingHandle.current = true;
        startY.current = e.touches[0].clientY;
      } else if (isScrollable) {
        const scrollTop = isScrollable.scrollTop;
        if (scrollTop > 0) {
          isDraggingHandle.current = false;
          return;
        }
        isDraggingHandle.current = true;
        startY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (!isDraggingHandle.current) return;
      
      currentY.current = e.touches[0].clientY;
      const diff = currentY.current - startY.current;
      
      if (diff > 0 && sheetRef.current) {
        e.preventDefault();
        sheetRef.current.style.transform = `translateY(${diff}px)`;
      }
    };

    const handleTouchEnd = () => {
      if (!isDraggingHandle.current) return;
      
      const diff = currentY.current - startY.current;
      
      if (diff > 100) {
        onClose();
      }
      
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(0)';
      }
      
      isDraggingHandle.current = false;
    };

    const sheet = sheetRef.current;
    if (sheet) {
      sheet.addEventListener('touchstart', handleTouchStart, { passive: true });
      sheet.addEventListener('touchmove', handleTouchMove, { passive: false });
      sheet.addEventListener('touchend', handleTouchEnd);

      return () => {
        sheet.removeEventListener('touchstart', handleTouchStart);
        sheet.removeEventListener('touchmove', handleTouchMove);
        sheet.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isOpen, onClose]);

  // Add a body class while the sheet is mounted so global UI (like floating nav)
  // can adjust (for example lowering z-index) when a modal is open.
  useEffect(() => {
    // Component only mounts when isOpen is true (we return null otherwise),
    // so it's safe to add the class here and remove on unmount.
    document.body.classList.add('bottom-sheet-open');
    return () => {
      document.body.classList.remove('bottom-sheet-open');
    };
  }, []);

  // When opening, ensure the content is scrolled to top and allow CSS
  // animation to run that makes the inner content appear coming from the top
  // while the sheet itself slides up from the bottom.
  useEffect(() => {
    if (!isOpen) return;
    // Small timeout to allow DOM to render the content before manipulating
    // scroll or triggering reflow for animations.
    const t = setTimeout(() => {
      const content = sheetRef.current && sheetRef.current.querySelector('.bottom-sheet-content');
      if (content) {
        // reset scroll to top so content animation begins from the top
        content.scrollTop = 0;
        // Force reflow to ensure CSS animation on insertion runs reliably
        // (not strictly necessary but helps on some Android webviews)
        // eslint-disable-next-line no-unused-expressions
        content.offsetHeight;
      }
    }, 20);

    return () => clearTimeout(t);
  }, [isOpen]);

  

  if (!isOpen) return null;

  return (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div 
        ref={sheetRef}
        className={`bottom-sheet ${isOpen ? 'open' : ''}`}
        style={height ? { height } : {}}
      >
        <div ref={handleRef} className="bottom-sheet-handle" />
        {title && (
          <div className="bottom-sheet-header">
            <h2>{title}</h2>
            <button className="bottom-sheet-close" onClick={onClose}>
              ✕
            </button>
          </div>
        )}
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </>
  );
}

export default BottomSheet;
